/**
 * Petits formatteurs purs pour l'onglet Progression / Dose / Évaluations de
 * la fiche élève (SLX-078). Aucune valeur littérale de couleur ici — le
 * mapping renvoie un « ton » consommé par `Badge` (classes CSS `theme.css`).
 */
import type { Matiere, NiveauMaitrise } from '@silex/shared';

export type BadgeTone = 'acquis' | 'en-cours' | 'a-revoir';

/**
 * Mapping `niveauMaitrise` (5 valeurs du moteur adaptatif) → 3 statuts
 * affichés (vocabulaire normalisé du design system : acquis / en cours / à
 * revoir) :
 *  - `maitrise` → **Acquis** : seul le niveau maximal compte comme acquis.
 *  - `en_voie_de_maitrise` / `en_consolidation` → **En cours** : la pratique
 *    progresse, pas encore stabilisée.
 *  - `en_decouverte` / `non_evalue` → **À revoir** : signal faible ou
 *    inexistant, c'est le panier surveillé par les recommandations
 *    d'intervention tuteur (cf. `packages/api/.../suivi.service.ts`).
 */
export function niveauMaitriseBadge(niveau: NiveauMaitrise): { tone: BadgeTone; label: string } {
  switch (niveau) {
    case 'maitrise':
      return { tone: 'acquis', label: 'Acquis' };
    case 'en_voie_de_maitrise':
    case 'en_consolidation':
      return { tone: 'en-cours', label: 'En cours' };
    case 'en_decouverte':
    case 'non_evalue':
      return { tone: 'a-revoir', label: 'À revoir' };
    default:
      return { tone: 'a-revoir', label: 'À revoir' };
  }
}

const MATIERE_LABELS: Record<Matiere, string> = {
  francais: 'Français',
  mathematiques: 'Mathématiques',
};

export function matiereLabel(matiere: Matiere): string {
  return MATIERE_LABELS[matiere] ?? matiere;
}

const DATE_COURTE = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/** « 30 août 2026 ». */
export function formatDateCourte(iso: string): string {
  return DATE_COURTE.format(new Date(iso));
}

/** « il y a 3 jours » / « aujourd'hui » / « hier ». */
export function formatDepuis(jours: number): string {
  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return 'hier';
  return `il y a ${jours} jours`;
}
